import { usePostHog } from 'posthog-js/react';

// Only the events that are actually used
export const POSTHOG_EVENTS = {
  // Page engagement (used by ScrollTracking)
  PAGE_VIEW: 'page_view',
  PAGE_SCROLL_25: 'page_scroll_25_percent',
  PAGE_SCROLL_75: 'page_scroll_75_percent',
  PAGE_SCROLL_BOTTOM: 'page_scroll_bottom',

  // CTA interactions (used by HeroSection)
  CTA_CLICK: 'cta_click',

  // Engagement score (used by EngagementTracker)
  ENGAGEMENT_SCORE: 'engagement_score',

  // Prismic-driven timed popup (used by PopupModal)
  POPUP_SHOWN: 'popup_shown',
  POPUP_DISMISSED: 'popup_dismissed',
  POPUP_CTA_CLICK: 'popup_cta_click',
  POPUP_MORE_INFO_CLICK: 'popup_more_info_click',
  POPUP_FORM_SUBMIT: 'popup_form_submit',

  // Calculator interactions (used by PricingCalculatorModal)
  CALCULATOR_MODAL_OPENED: 'calculator_modal_opened',
  CALCULATOR_STARTED: 'calculator_started',
  CALCULATOR_CHECKOUT: 'calculator_checkout',
  CALCULATOR_COMPLETED: 'calculator_completed',

  // Per-step funnel and capture (used by the NetSuite pricing complexity game)
  CALCULATOR_STEP_VIEWED: 'calculator_step_viewed',
  CALCULATOR_STEP_ANSWERED: 'calculator_step_answered',
  CALCULATOR_CHECKLIST_SENT: 'calculator_checklist_sent',
} as const;

/**
 * True when this visitor is flagged as internal traffic (the `traffic_type`
 * cookie). Exported so every analytics path — PostHog and GTM alike — can apply
 * the same exclusion; staff clicks otherwise inflate conversion rates.
 */
export function isInternalTraffic(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie
    .split(';')
    .some((cookie) => cookie.trim().startsWith('traffic_type=internal'));
}

// Utility hook for PostHog tracking
export function usePostHogTracking() {
  const posthog = usePostHog();

  const trackEvent = (
    eventName: string,
    properties?: Record<string, string | number | boolean>
  ) => {
    if (!posthog) return;

    if (isInternalTraffic()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('PostHog event blocked: internal traffic detected', eventName, properties);
      }
      return;
    }

    posthog.capture(eventName, {
      timestamp: new Date().toISOString(),
      ...properties,
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('PostHog event tracked:', eventName, properties);
    }
  };

  const trackPageView = (
    pageName: string,
    additionalProperties?: Record<string, string | number | boolean>
  ) => {
    trackEvent(POSTHOG_EVENTS.PAGE_VIEW, {
      page_name: pageName,
      ...additionalProperties,
    });
  };

  const trackCTAClick = (
    ctaName: string,
    location: string,
    additionalProperties?: Record<string, string | number | boolean>
  ) => {
    trackEvent(POSTHOG_EVENTS.CTA_CLICK, {
      cta_name: ctaName,
      location: location,
      ...additionalProperties,
    });
  };

  return {
    trackEvent,
    trackPageView,
    trackCTAClick,
  };
}
